<app-billytics-public-container-component>
  <div class="min-h-screen py-12 px-4 sm:px-6 lg:px-8">
    <!-- Header -->
    <div class="max-w-4xl mx-auto text-center mb-10">
      <h1 class="text-3xl font-bold">Terms & Conditions</h1>
      <p class="mt-2 text-sm">Last updated: September 2025</p>
    </div>

    <!-- Card -->
    <mat-card class="max-w-4xl mx-auto">
      <mat-card-content>
        <section>
          <p>
            These Terms and Conditions (“Terms”) govern your access to and use of the Billytics
            AI-based application (“Service”), provided by Billytics (“we,” “our,” or “us”). By
            accessing or using the Service, you agree to comply with and be bound by these Terms. If
            you do not agree, please do not use the Service.
          </p>
        </section>

        <!-- 1 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">1. Acceptance of Terms</h2>
          <p>
            By using the Service, you confirm that you have read, understood, and agree to be bound
            by these Terms. If not, refrain from using the Service.
          </p>
        </section>

        <!-- 2 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">2. Definitions</h2>
          <ul class="list-disc list-inside space-y-1">
            <li><b>Service</b>: The AI-based application developed and provided by Billytics.</li>
            <li><b>User/You</b>: The individual or entity using the Service.</li>
            <li><b>We/Our/Billytics</b>: The company providing the Service.</li>
            <li>
              <b>Data</b>: Any personal, financial, or non-personal data provided or collected.
            </li>
          </ul>
        </section>

        <!-- 3 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">3. Eligibility and Registration</h2>
          <p>
            <b>Age Requirement:</b> You must be at least 18 or the legal age of majority in your
            jurisdiction.
          </p>
          <p>
            <b>Account Creation:</b> You must provide accurate information when creating an account.
          </p>
          <p>
            <b>Account Security:</b> You are responsible for safeguarding credentials and notifying
            us of unauthorized access.
          </p>
        </section>

        <!-- 4 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">4. License to Use the Service</h2>
          <p>
            <b>License Grant:</b> A limited, non-exclusive, non-transferable, revocable license to
            use the Service lawfully.
          </p>
          <p>
            <b>Restrictions:</b> No reverse-engineering, unlawful activities, or unauthorized
            access.
          </p>
        </section>

        <!-- 5 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">5. Data Collection and Use</h2>
          <p>
            <b>User Data:</b> By using the Service, you consent to collection and storage as
            outlined in our Privacy Policy.
          </p>
          <p>
            <b>Purpose:</b> Data is used to improve services, personalize experiences, and enhance
            AI algorithms.
          </p>
          <p>
            <b>Storage:</b> Data is encrypted, stored with AWS, and may be shared with third parties
            per our Privacy Policy.
          </p>
          <p>
            <b>Data Rights:</b> You retain ownership but grant us a license to use it for providing
            services. Deletion requests are supported.
          </p>
          <p><b>Email Monitoring:</b> Billytics only monitors email when explicitly allowed.</p>
          <p>
            <b>AI Data Processing:</b> Data may be processed with Gemini for extraction and
            analysis.
          </p>
          <p><b>Analytics:</b> Metadata and usage are collected through Google Analytics.</p>
        </section>

        <!-- 6 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">6. User Responsibilities</h2>
          <p><b>Lawful Use:</b> You must comply with applicable laws and these Terms.</p>
          <p>
            <b>Prohibited Activities:</b> No violations, malware, interference, or abuse of the
            Service.
          </p>
        </section>

        <!-- 7 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">7. Global Data Transfer</h2>
          <p>
            Your data may be transferred and stored outside your country. We comply with laws, but
            protections vary by jurisdiction.
          </p>
        </section>

        <!-- 8 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">8. Pricing and Payment</h2>
          <p>
            <b>Prepaid Service:</b> Fees must be paid upfront and are non-refundable unless
            otherwise stated.
          </p>
          <p><b>Billing:</b> You agree to pay applicable fees plus taxes depending on your plan.</p>
        </section>

        <!-- 9 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">9. Refund and Cancellation Policy</h2>
          <p>
            <b>Refund Policy:</b> Prepaid service is non-refundable unless explicitly offered at
            purchase.
          </p>
          <p>
            <b>Cancellations:</b> You may cancel anytime, but fees are not refunded for the current
            billing period.
          </p>
        </section>

        <!-- 10 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">10. Intellectual Property</h2>
          <p>
            <b>Ownership:</b> All content, trademarks, and IP are owned by Billytics or licensors.
          </p>
          <p><b>Limited License:</b> Granted only for access in accordance with these Terms.</p>
        </section>

        <!-- 11 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">11. Third-Party Services and Integrations</h2>
          <p>
            <b>Providers:</b> Service may integrate with third parties (e.g., Google Analytics,
            Stripe, AWS).
          </p>
          <p>
            <b>No Responsibility:</b> Billytics is not liable for third-party content or actions.
          </p>
        </section>

        <!-- 12 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">12. Service Availability and Disruptions</h2>
          <p>
            <b>Downtime:</b> Service may be unavailable due to maintenance or factors beyond
            control.
          </p>
          <p><b>No Liability:</b> Billytics is not responsible for damages from interruptions.</p>
        </section>

        <!-- 13 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">13. Force Majeure</h2>
          <p>
            Billytics is not liable for failures caused by events beyond control, such as natural
            disasters, strikes, or technical failures.
          </p>
        </section>

        <!-- 14 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">14. Account Suspension and Deletion</h2>
          <p>
            <b>Right to Suspend/Delete:</b> We may suspend or delete accounts at our discretion.
          </p>
          <p><b>No Compensation:</b> Suspension or deletion provides no right to compensation.</p>
          <p><b>Payment Obligation:</b> Users remain responsible for outstanding fees.</p>
        </section>

        <!-- 15 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">15. Dispute Resolution</h2>
          <p><b>Complaints:</b> Contact us at team@billytics.pro for disputes.</p>
          <p>
            <b>Online Dispute Resolution:</b> Issues may also be resolved through ODR platforms.
          </p>
        </section>

        <!-- 16 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">16. Limitation of Liability</h2>
          <p>
            Billytics is not liable for indirect damages, loss of profits, data, or goodwill.
            Liability is capped at fees paid in the last six months.
          </p>
        </section>

        <!-- 17 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">17. Indemnification</h2>
          <p>
            You agree to indemnify and hold harmless Billytics, its employees, and agents from
            claims or damages arising from your use of the Service.
          </p>
        </section>

        <!-- 18 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">18. Changes to Terms</h2>
          <p>
            We may modify these Terms anytime. Changes are effective upon posting. Continued use
            constitutes acceptance.
          </p>
        </section>

        <!-- 19 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">19. Governing Law</h2>
          <p>
            These Terms are governed by the laws of Ontario, Canada. Disputes will be resolved in
            Ottawa courts.
          </p>
        </section>

        <!-- 20 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">20. Severability</h2>
          <p>
            If any provision is unenforceable, the remaining provisions remain valid and
            enforceable.
          </p>
        </section>

        <!-- 21 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">21. Entire Agreement</h2>
          <p>
            These Terms constitute the entire agreement and supersede prior agreements or
            understandings.
          </p>
        </section>

        <!-- 22 -->
        <section>
          <h2 class="text-lg font-semibold mb-3">22. Feedback and Suggestions</h2>
          <p>
            Any feedback or suggestions provided become the exclusive property of Billytics and may
            be used without obligation.
          </p>
        </section>

        <!-- Footer -->
        <div class="border-t pt-6">
          <p class="text-sm">
            For questions or concerns, please contact us at:
            <a href="mailto:info@billytics.pro" class="text-blue-600 hover:underline"
              >info@billytics.pro</a
            >
          </p>
        </div>
      </mat-card-content>
    </mat-card>
    <!-- </div> -->
  </div>
</app-billytics-public-container-component>
